import React, { useCallback, useContext, useEffect, useState } from 'react';
import RecorderDrawerContext from '../../Context';
import {
  RecorderState,
  RecordingMetadata,
  Thumbnail,
  VideoRecorder,
} from '@vimily/bonjoro-ui-components-v2/VideoRecorder';
import cx from 'classnames';
import { UploadErrorType, Uploader, errors } from './Uploader';
import { useDelayedState } from '@vimily/bonjoro-ui-components-v2/hooks/use-delayed-state-hook';
import Config from '~/Config';
import {
  AnimatedDiv,
  HideAnimation,
  ShowAnimation,
} from '@vimily/bonjoro-ui-components-v2/AnimatedDiv';

export function Recorder() {
  const {
    recorderState,
    recorderControl,
    setRecorderState,
    setVideoBlob,
    setSelectedThumbnailIndex,
    setGeneratedThumbnails,
    setIsExternalVideo,
    isExternalVideo,
    onRecordingStart,
    onRecordingStop,
    onRecordingComplete: onRecordingCompleteCallback,
    onRecorderSettingsClick,
    onRecorderSettingsCloseClick,
    onFileUpload,
  } = useContext(RecorderDrawerContext);

  useEffect(() => {
    if (recorderState == RecorderState.Recording) {
      onRecordingStart?.();
    }
  }, [recorderState]);

  const [showUploader, setShowUploader] = useState<boolean>(false);
  const [file, setFile] = useState<File>();
  const [errorType, setErrorType] = useState<UploadErrorType>();
  const delayedShowUploader = useDelayedState(showUploader);

  const clearError = () => setErrorType(undefined);

  const handleFileUpload = (files: File[]) => {
    const file = files[0];

    if (file.size > Config.FILE_UPLOAD_MAX_SIZE_B) {
      setErrorType(UploadErrorType.FileTooLarge);
      return;
    }

    if (!file.type.startsWith('video')) {
      setErrorType(UploadErrorType.UnsupportedFile);
      return;
    }

    clearError();
    setFile(files[0]);
    setShowUploader(false);
    setIsExternalVideo(true);

    onFileUpload?.(files);
  };

  const handleDragOver = (event: DragEvent) => {
    event.preventDefault();
    event.stopPropagation();

    if (!showUploader) {
      setShowUploader(true);
    }
  };

  useEffect(() => {
    document.addEventListener('dragover', handleDragOver);

    return () => {
      document.removeEventListener('dragover', handleDragOver);
    };
  }, []);

  const onCloseClick = () => {
    setShowUploader(false);
    clearError();
  };

  const onRecordingComplete = useCallback(
    (data: Blob, thumbnails: Thumbnail[], metadata?: RecordingMetadata) => {
      onRecordingCompleteCallback?.({ data, thumbnails, metadata });
      setVideoBlob(data);
      setGeneratedThumbnails(thumbnails);
      setSelectedThumbnailIndex(0);

      if (!isExternalVideo) {
        onRecordingStop?.({ data, thumbnails, metadata });
      }
    },
    [isExternalVideo]
  );

  const onSettingsClick = () => {
    onRecorderSettingsClick?.();
  };

  const onSettingsCloseClick = (
    videoDevice?: MediaDeviceInfo,
    audioDevice?: MediaDeviceInfo
  ) => {
    onRecorderSettingsCloseClick?.(videoDevice, audioDevice);
  };

  const recorderLoading = [
    RecorderState.WaitingForDevices,
    RecorderState.Loading,
    RecorderState.Init,
  ].includes(recorderState);

  const hideUploader = [
    RecorderState.Recording,
    RecorderState.GenerateThumbnails,
    RecorderState.Complete,
  ].includes(recorderState);

  return (
    <>
      <div
        className={cx(
          'md:py-4 !w-full !h-full flex flex-col items-center flex-1',
          {
            'hidden w-0 h-0 absolute': delayedShowUploader,
          }
        )}
      >
        <VideoRecorder
          className='!w-full !h-full md:!w-[500px] md:!h-[500px] p-0'
          ref={recorderControl}
          onRecordingComplete={onRecordingComplete}
          onSettingsClick={onSettingsClick}
          onSettingsClose={onSettingsCloseClick}
          onRecorderStateChange={setRecorderState}
          containerClassName='rounded-2xl'
          manualUpload={file}
        />
      </div>
      {errorType && (
        <div
          className={cx([
            'flex flex-col v2-text-orange-600 text-[21px] absolute text-center ',
            'font-bold leading-8 z-99 top-[15%] w-[400px] v2-animate-fade-in-150',
          ])}
        >
          {errors[errorType]}
        </div>
      )}
      {delayedShowUploader ? (
        <AnimatedDiv
          show={showUploader}
          className='w-full h-full'
          showAnimation={ShowAnimation.FadeIn150}
          hideAnimation={HideAnimation.FadeOut150}
        >
          <Uploader
            onFileUpload={handleFileUpload}
            onCloseClick={onCloseClick}
            className={cx({
              'v2-animate-fade-in-150': showUploader,
              'v2-animate-fade-out-150': !showUploader,
            })}
          />
        </AnimatedDiv>
      ) : (
        <AnimatedDiv
          show={!hideUploader}
          className='flex flex-col gap-y-3 items-center flex-1 mb-6'
          showAnimation={ShowAnimation.FadeIn150}
          hideAnimation={HideAnimation.FadeOut150}
        >
          <button
            className={cx(
              [
                'v2-text-neutral-300 underline hover:underline',
                'text-base border-none bg-transparent',
              ],
              {
                'hover:!v2-text-neutral-300 !cursor-not-allowed':
                  recorderLoading,
                'hover:!v2-text-neutral-900 !cursor-pointer': !recorderLoading,
              }
            )}
            disabled={recorderLoading}
            onClick={() => setShowUploader(true)}
          >
            Upload a video instead
          </button>
          <div className='flex flex-col gap-y-0.5 v2-text-neutral-500 items-center'>
            <span className='v2-text-neutral-500 text-xs'>
              Max video size: {Config.FILE_UPLOAD_MAX_SIZE_MB}MB
            </span>
            <span className='v2-text-neutral-500 text-xs'>
              Acceptable format: MP4 or WebM
            </span>
          </div>
        </AnimatedDiv>
      )}
    </>
  );
}
